import { bridge } from '../bridge'

// null = not resolved yet. Empty string is a valid resolution (means
// "same origin", used for Vite-proxied in-browser dev).
let cached: string | null = null

/// Resolves the server base URL. In-browser dev defaults to
/// http://localhost:8080 (where permit-cloud runs locally). In the native
/// shell we ask for app.info and key off the bundle ID: dev builds
/// (*.dev) hit the dev server, AppStore builds hit the prod server. Prod
/// and dev currently share the same hostname per CLAUDE.md's dev-instance
/// section, but keeping the switch explicit lets us change prod later
/// without touching callers.
export async function serverBaseURL(): Promise<string> {
  if (cached !== null) return cached
  if (!bridge.isNative) {
    // In-browser dev: empty string keeps fetch() calls same-origin so
    // they route through Vite's dev proxy (configured in vite.config.ts)
    // which forwards to permit-cloud. Avoids CORS preflights and doesn't
    // require the server to know about the web origin.
    cached = ''
    return cached
  }
  // Native shell. When loaded from a real http(s):// dev server (Vite
  // via HEARTH_WEB_URL / 10.0.2.2 / localhost) use same-origin so
  // fetches route through Vite's proxy to whatever permit-cloud the dev
  // is targeting (HEARTH_API). Bundled mode on Android loads under the
  // WebViewAssetLoader virtual host (also https), but there's no proxy
  // there — treat it like file:// and resolve against the hosted server.
  const onProxiedOrigin =
    (location.protocol === 'http:' || location.protocol === 'https:') &&
    location.hostname !== 'appassets.androidplatform.net'
  if (onProxiedOrigin) {
    cached = ''
    return cached
  }
  const info = await bridge.call('app.info')
  // iOS encodes dev-vs-prod in the bundle ID suffix (`.dev`) because the
  // Apple build configs swap entitlements via Info.plist. Android keeps a
  // single package name and signals build flavor via `buildConfig`. Treat
  // either signal as "dev".
  const isDev = info.bundleID.endsWith('.dev') || info.buildConfig === 'debug'
  cached = isDev ? 'https://api.hearthcmd.dev' : 'https://api.hearthcmd.com'
  return cached
}

/// Override for local dev against a localhost server inside the shell
/// (Simulator only — physical devices can't reach the Mac's localhost).
export function overrideBaseURL(url: string) {
  cached = url
}

/// Returns the Hearth setup guide URL for the current build environment.
/// Reads the already-cached base URL (populated after the first WS connect);
/// falls back to prod if the cache is cold (e.g. rendered before connect).
export function getSetupGuideURL(): string {
  const base = cached ?? 'https://api.hearthcmd.com'
  return base.includes('.dev')
    ? 'https://www.hearthcmd.dev/guide.html'
    : 'https://www.hearthcmd.com/guide.html'
}
